import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Image, Typography, theme } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { ProfileOut, UserOut } from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ProfileRoleTag } from '@/components/iam/tags';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

interface ProfileGeneralTabProps {
  record: ProfileOut;
  user?: UserOut;
}

export const ProfileGeneralTab = ({ record, user }: ProfileGeneralTabProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const verticalDescProps = useVerticalDescProps();

  const hasSettings =
    record.settings && Object.keys(record.settings).length > 0;

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: <Text code>{record.id}</Text>,
    },
    {
      key: 'user',
      label: t('account.profiles.fields.user'),
      children: (
        <ShowLink resource="iam.users" id={record.user_id}>
          {user?.email ?? user?.username ?? record.user_id}
        </ShowLink>
      ),
    },
    {
      key: 'account_id',
      label: t('account.profiles.fields.account_id'),
      children: orDash(record.account_id),
    },
    {
      key: 'role',
      label: t('account.profiles.fields.role'),
      children: <ProfileRoleTag role={record.role} />,
    },
    {
      key: 'name',
      label: t('account.profiles.fields.name'),
      children: orDash(record.name),
    },
    {
      key: 'avatar',
      label: t('account.profiles.fields.avatar_url'),
      children: orDash(
        record.avatar_url ? <Image src={record.avatar_url} height={80} /> : null
      ),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
    ...(record.deleted_at
      ? [
          {
            key: 'deleted_at',
            label: t('account.profiles.fields.deleted_at'),
            children: orDash(formatDateTime(record.deleted_at)),
          },
        ]
      : []),
    {
      key: 'settings',
      label: t('account.profiles.fields.settings'),
      span: 3,
      children: orDash(
        hasSettings ? (
          <pre style={{ margin: 0, fontSize: token.fontSizeSM }}>
            {JSON.stringify(record.settings, null, 2)}
          </pre>
        ) : null
      ),
    },
  ];

  return (
    <Card title={t('base.sections.basic_info')}>
      <Descriptions
        {...verticalDescProps}
        column={{ xs: 1, sm: 2, md: 3 }}
        items={items}
      />
    </Card>
  );
};
